import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Clock, CreditCard, AlertTriangle, Pause } from "lucide-react";
import { Card, CardBody, CardHeader } from "../../components/Card";
import { api } from "../../lib/api";
import type { AdminStats } from "../../lib/me";

export default function AdminOverview() {
  const [stats, setStats] = useState<AdminStats | null>(null);

  useEffect(() => {
    api.get<AdminStats>("/api/admin/stats").then(setStats).catch(() => setStats(null));
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Overview</h1>
        <p className="mt-1 text-sm text-gray-500">System-wide tenant snapshot.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total tenants" value={stats?.counts.total ?? "—"} icon={Users} />
        <StatCard label="Trial active" value={stats?.counts.trialActive ?? "—"} icon={Clock} />
        <StatCard label="Paid" value={stats?.counts.paid ?? "—"} icon={CreditCard} />
        <StatCard label="Suspended" value={stats?.counts.suspended ?? "—"} icon={Pause} />
      </div>

      <Card>
        <CardHeader title="Trials expiring in the next 3 days" actions={
          <Link to="/admin/tenants?filter=expiring" className="text-sm font-medium text-gray-700 hover:text-gray-900">
            View all →
          </Link>
        } />
        <CardBody>
          {!stats || stats.counts.expiringSoon === 0 ? (
            <p className="text-sm text-gray-500">No trials expiring soon.</p>
          ) : (
            <p className="text-sm text-gray-700">
              <strong>{stats.counts.expiringSoon}</strong> tenant
              {stats.counts.expiringSoon !== 1 ? "s have" : " has"} a trial expiring in the next 3 days.{" "}
              <Link to="/admin/tenants" className="font-medium text-gray-900 underline">Manage tenants →</Link>
            </p>
          )}
        </CardBody>
      </Card>

      {stats && (
        <Card>
          <CardHeader title="Aggregate usage" />
          <CardBody>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex items-baseline justify-between">
                <span>Total quota across tenants</span>
                <span className="font-mono font-semibold text-gray-900">
                  {stats.usage.totalQuota.toLocaleString()} emails
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <span>Total quota used</span>
                <span className="font-mono font-semibold text-gray-900">
                  {stats.usage.totalUsed.toLocaleString()} emails
                </span>
              </div>
              {stats.usage.totalQuota > 0 && (
                <div className="flex items-baseline justify-between">
                  <span>Utilization</span>
                  <span className="font-mono font-semibold text-gray-900">
                    {Math.round((stats.usage.totalUsed / stats.usage.totalQuota) * 100)}%
                  </span>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon }: { label: string; value: number | string; icon: typeof Users }) {
  return (
    <Card>
      <CardBody className="p-5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-500">{label}</span>
          <Icon className="h-4 w-4 text-gray-400" />
        </div>
        <div className="mt-3 text-3xl font-semibold tracking-tight text-gray-900">{value}</div>
      </CardBody>
    </Card>
  );
}

void AlertTriangle;
